import { type BillCategory, BillCategoryNames } from '@fruitful/types';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React from 'react';
import { ScrollView } from 'react-native';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    FootnoteRegular,
    XStack,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import {
    centsToFormattedDollars,
    formatToSimpleDateString,
} from '../../../common/utils/formatting';
import { CopyableTextField } from '../../../member/components/renderers/CopyableTextField';
import type { Bill, BillTransaction } from '../../entityTypes';

const emDash = '—';

const formatOptional = (value: string | null | undefined): string =>
    value != null && value !== '' ? value : emDash;

const formatOptionalDate = (value: string | null | undefined): string =>
    value != null && value !== ''
        ? (formatToSimpleDateString(value) ?? value)
        : emDash;

const categoryLabel = (category?: string): string => {
    if (category == null || category === '') return emDash;
    const key = category.toLowerCase();
    if (key in BillCategoryNames) {
        return BillCategoryNames[key as BillCategory];
    }
    return category;
};

const DetailRow = ({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) => (
    <XStack
        justifyContent="space-between"
        alignItems="flex-start"
        gap={16}
        flexWrap="wrap"
    >
        <BodySmallEmphasized color="$gray1000" minWidth={140}>
            {label}
        </BodySmallEmphasized>
        <YStack flex={1} alignItems="flex-end" minWidth={200}>
            {children}
        </YStack>
    </XStack>
);

const TransactionAmountBody = (row: BillTransaction) => (
    <BodySmallRegular>
        {centsToFormattedDollars(row.transactionAmount)}
    </BodySmallRegular>
);

const TransactionIdBody = (row: BillTransaction) => {
    const id = row.transactionId;
    if (id == null || id === '') {
        return <BodySmallRegular>{emDash}</BodySmallRegular>;
    }
    return (
        <CopyableTextField value={id}>
            <BodySmallRegular selectable>{id}</BodySmallRegular>
        </CopyableTextField>
    );
};

export const BillDetailModal = ({
    bill,
    isOpen,
    onClose,
}: {
    bill: Bill | null;
    isOpen: boolean;
    onClose: () => void;
}) => {
    if (bill == null) {
        return null;
    }

    const transactions = bill.transactions ?? [];
    const jsonRecord = JSON.stringify(bill, null, 2);

    return (
        <GroveSimpleModal isOpen={isOpen} onClose={onClose} hasCloseButton>
            <ScrollView style={{ maxHeight: '85vh' }}>
                <YStack gap={16} padding={16} maxWidth={720}>
                    <BodyLargeEmphasized>Bill details</BodyLargeEmphasized>

                    <YStack gap={12}>
                        <DetailRow label="ID">
                            <CopyableTextField value={bill.id}>
                                <BodyMediumRegular selectable>
                                    {bill.id}
                                </BodyMediumRegular>
                            </CopyableTextField>
                        </DetailRow>
                        <DetailRow label="AI bill ID">
                            {bill.aiBillId != null && bill.aiBillId !== '' ? (
                                <CopyableTextField value={bill.aiBillId}>
                                    <BodyMediumRegular selectable>
                                        {bill.aiBillId}
                                    </BodyMediumRegular>
                                </CopyableTextField>
                            ) : (
                                <BodyMediumRegular>{emDash}</BodyMediumRegular>
                            )}
                        </DetailRow>
                        <DetailRow label="Membership ID">
                            <CopyableTextField value={bill.membershipId}>
                                <BodyMediumRegular selectable>
                                    {bill.membershipId}
                                </BodyMediumRegular>
                            </CopyableTextField>
                        </DetailRow>
                        <DetailRow label="Account ID">
                            <CopyableTextField value={bill.accountId}>
                                <BodyMediumRegular selectable>
                                    {bill.accountId}
                                </BodyMediumRegular>
                            </CopyableTextField>
                        </DetailRow>
                        <DetailRow label="User ID">
                            {bill.userId != null && bill.userId !== '' ? (
                                <CopyableTextField value={bill.userId}>
                                    <BodyMediumRegular selectable>
                                        {bill.userId}
                                    </BodyMediumRegular>
                                </CopyableTextField>
                            ) : (
                                <BodyMediumRegular>{emDash}</BodyMediumRegular>
                            )}
                        </DetailRow>
                        <DetailRow label="Display name">
                            <BodyMediumRegular>
                                {formatOptional(
                                    bill.displayName ?? bill.name ?? bill.payee,
                                )}
                            </BodyMediumRegular>
                        </DetailRow>
                        <DetailRow label="Name">
                            <BodyMediumRegular>
                                {formatOptional(bill.name)}
                            </BodyMediumRegular>
                        </DetailRow>
                        <DetailRow label="Payee">
                            <BodyMediumRegular>
                                {formatOptional(bill.payee)}
                            </BodyMediumRegular>
                        </DetailRow>
                        <DetailRow label="Description">
                            <BodyMediumRegular>
                                {formatOptional(bill.description)}
                            </BodyMediumRegular>
                        </DetailRow>
                        <DetailRow label="Category">
                            <BodyMediumRegular>
                                {categoryLabel(bill.category)}
                            </BodyMediumRegular>
                        </DetailRow>
                        <DetailRow label="Amount">
                            <BodyMediumRegular>
                                {centsToFormattedDollars(bill.amount)}
                            </BodyMediumRegular>
                        </DetailRow>
                        <DetailRow label="Monthly amount (derived)">
                            <BodyMediumRegular>
                                {centsToFormattedDollars(bill.monthlyAmount)}
                            </BodyMediumRegular>
                        </DetailRow>
                        <DetailRow label="Cadence">
                            <BodyMediumRegular>
                                {bill.cadence}
                            </BodyMediumRegular>
                        </DetailRow>
                        <DetailRow label="Timing label">
                            <BodyMediumRegular>
                                {formatOptional(bill.timingLabel)}
                            </BodyMediumRegular>
                        </DetailRow>
                        <DetailRow label="Timing description">
                            <BodyMediumRegular>
                                {formatOptional(bill.timingDescription)}
                            </BodyMediumRegular>
                        </DetailRow>
                        <DetailRow label="Creation type">
                            <BodyMediumRegular>
                                {bill.creationType}
                            </BodyMediumRegular>
                        </DetailRow>
                        <DetailRow label="Activation status">
                            <BodyMediumRegular>
                                {bill.activationStatus}
                            </BodyMediumRegular>
                        </DetailRow>
                        <DetailRow label="Created at">
                            <BodyMediumRegular>
                                {formatOptionalDate(bill.createdAt)}
                            </BodyMediumRegular>
                        </DetailRow>
                        <DetailRow label="Last paid at">
                            <BodyMediumRegular>
                                {formatOptionalDate(bill.lastPaidAt)}
                            </BodyMediumRegular>
                        </DetailRow>
                        <DetailRow label="Removed at">
                            <BodyMediumRegular>
                                {formatOptionalDate(bill.removedAt)}
                            </BodyMediumRegular>
                        </DetailRow>
                    </YStack>

                    <YStack
                        gap={12}
                        paddingTop={8}
                        borderTopWidth={1}
                        borderColor="$gray200"
                    >
                        <BodyLargeEmphasized>
                            Transactions on this bill
                        </BodyLargeEmphasized>
                        <BodySmallRegular color="$gray1000">
                            Payments and matches tied to this bill in Fruitful
                            (when the API includes them).
                        </BodySmallRegular>
                        {transactions.length === 0 ? (
                            <BodyMediumRegular
                                fontStyle="italic"
                                color="$gray1000"
                            >
                                No transaction records on this bill. Grove often
                                strips transaction arrays on the membership
                                payload; check the JSON below if the API sent
                                any.
                            </BodyMediumRegular>
                        ) : (
                            <DataTable
                                value={transactions}
                                size="small"
                                tableStyle={{ width: '100%' }}
                                scrollable
                                scrollHeight="360px"
                            >
                                <Column
                                    field="transactionDate"
                                    header="Posted date"
                                    body={(row: BillTransaction) =>
                                        formatOptionalDate(row.transactionDate)
                                    }
                                />
                                <Column
                                    field="vendor"
                                    header="Vendor"
                                    body={(row: BillTransaction) =>
                                        formatOptional(row.vendor)
                                    }
                                />
                                <Column
                                    field="transactionAmount"
                                    header="Amount"
                                    body={TransactionAmountBody}
                                />
                                <Column
                                    field="status"
                                    header="Status"
                                    body={(row: BillTransaction) =>
                                        formatOptional(row.status)
                                    }
                                />
                                <Column
                                    field="transactionId"
                                    header="Transaction ID"
                                    body={TransactionIdBody}
                                />
                                <Column
                                    field="institutionName"
                                    header="Institution"
                                    body={(row: BillTransaction) =>
                                        formatOptional(row.institutionName)
                                    }
                                />
                                <Column
                                    field="accountMask"
                                    header="Account mask"
                                    body={(row: BillTransaction) =>
                                        formatOptional(row.accountMask)
                                    }
                                />
                                <Column
                                    field="dateAdded"
                                    header="Added"
                                    body={(row: BillTransaction) =>
                                        formatOptionalDate(row.dateAdded)
                                    }
                                />
                                <Column
                                    field="dateChanged"
                                    header="Changed"
                                    body={(row: BillTransaction) =>
                                        formatOptionalDate(row.dateChanged)
                                    }
                                />
                                <Column
                                    field="addedBy"
                                    header="Added by"
                                    body={(row: BillTransaction) =>
                                        formatOptional(row.addedBy)
                                    }
                                />
                                <Column
                                    field="hiddenBy"
                                    header="Hidden by"
                                    body={(row: BillTransaction) =>
                                        formatOptional(row.hiddenBy)
                                    }
                                />
                            </DataTable>
                        )}
                    </YStack>

                    <YStack gap={8}>
                        <BodySmallEmphasized color="$gray1000">
                            Full record (JSON)
                        </BodySmallEmphasized>
                        <FootnoteRegular
                            fontFamily="$monoBody"
                            style={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                            }}
                            selectable
                        >
                            {jsonRecord}
                        </FootnoteRegular>
                    </YStack>
                </YStack>
            </ScrollView>
        </GroveSimpleModal>
    );
};
